/**
 * /projects/:projectSlug/envs/:envSlug/secrets/new — "store your first secret" form.
 *
 * Project + env come from the URL, not from a global picker context. The
 * form takes:
 *
 *   - path: validated client-side (slash-separated segments matching
 *     [A-Za-z0-9_-]+, no leading/trailing slash, max 256 chars). Case is
 *     preserved, so `DATABASE_URL` and `database/url` are both valid — the
 *     import flow leans on this to round-trip pasted env vars verbatim.
 *     A live env-var preview ("prod/db/password" → "PROD_DB_PASSWORD") shows
 *     what shape this will land in the consumer's app.
 *   - value: tabbed input — Text (textarea) or File (drag/drop or picker).
 *     Files are base64-encoded before submit; max 64 KB either way.
 *   - content_type: select with sensible defaults (text/plain, JSON, octet,
 *     PEM). File uploads auto-detect by extension.
 *
 * On 201 we navigate to the detail page placeholder (WARD-78 backfills the
 * real one). On 409 we offer to jump to the existing path's detail page
 * instead — the "add as new version inline" flow waits on WARD-80.
 */

import { PageHeader } from "@/components/PageHeader";
import { Select } from "@/components/Select";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  type CreateSecretBody,
  type SecretMeta,
  VISIBILITY_DESCRIPTIONS,
  type Visibility,
  WardkeyAPIError,
  useActiveOrg,
  useApiMutation,
} from "@/lib/api";
import { useToast } from "@/lib/toast";
import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router";

const MAX_VALUE_BYTES = 64 * 1024;
const SEGMENT_RE = /^[A-Za-z0-9_-]+$/;
const MAX_PATH_LEN = 256;

const CONTENT_TYPES = [
  { value: "text/plain", label: "Plain text" },
  { value: "application/json", label: "JSON" },
  { value: "application/octet-stream", label: "Binary (octet-stream)" },
  { value: "application/x-pem-file", label: "PEM (cert / key)" },
] as const;

const EXT_TO_CONTENT_TYPE: Record<string, string> = {
  txt: "text/plain",
  json: "application/json",
  pem: "application/x-pem-file",
  crt: "application/x-pem-file",
  cer: "application/x-pem-file",
  key: "application/x-pem-file",
};

type ValueKind = "text" | "file";

type ValueState =
  | { kind: "text"; text: string }
  | { kind: "file"; name: string; size: number; base64: string };

export function SecretsNewRoute() {
  const activeOrg = useActiveOrg();
  const params = useParams<{ projectSlug: string; envSlug: string }>();
  const projectSlug = params.projectSlug ?? "";
  const envSlug = params.envSlug ?? "";

  if (!projectSlug || !envSlug) {
    return (
      <Centered>
        <p className="text-[14px] text-muted-foreground">
          Missing project or environment in the URL.{" "}
          <Link to="/projects" className="text-foreground underline-offset-4 hover:underline">
            Pick a project
          </Link>{" "}
          first.
        </p>
      </Centered>
    );
  }

  return <NewSecretForm orgId={activeOrg.org_id} project={projectSlug} env={envSlug} />;
}

function Centered({ children }: { children: React.ReactNode }) {
  return (
    <div className="mx-auto max-w-[700px]">
      <div className="fade-up rounded-xl border border-dashed border-border bg-card/40 p-10 text-center">
        {children}
      </div>
    </div>
  );
}

function NewSecretForm({
  orgId,
  project,
  env,
}: {
  orgId: string;
  project: string;
  env: string;
}) {
  const { pushToast } = useToast();
  const navigate = useNavigate();
  const [path, setPath] = useState("");
  const [valueKind, setValueKind] = useState<ValueKind>("text");
  const [value, setValue] = useState<ValueState>({ kind: "text", text: "" });
  const [contentType, setContentType] = useState<string>("text/plain");
  const [visibility, setVisibility] = useState<Visibility>("hidden");
  const [touched, setTouched] = useState(false);
  const [valueTouched, setValueTouched] = useState(false);
  const [conflict, setConflict] = useState<null | { path: string }>(null);
  const [serverError, setServerError] = useState<string | null>(null);

  // Reset value when the tab switches away from text — re-entering Text
  // shouldn't show a stale file blob, and switching to File should clear the
  // textarea so validation can re-check the (now empty) file slot.
  useEffect(() => {
    if (valueKind === "text") {
      setValue({ kind: "text", text: "" });
    } else {
      setValue({ kind: "file", name: "", size: 0, base64: "" });
    }
  }, [valueKind]);

  const pathError = useMemo(() => (touched ? validatePath(path) : null), [path, touched]);
  const valueError = useMemo(
    () => (valueTouched ? validateValue(value) : null),
    [value, valueTouched],
  );
  const envVarPreview = useMemo(() => pathToEnvVar(path), [path]);

  const create = useApiMutation<CreateSecretBody, SecretMeta>(
    (c, body) => c.secrets.create(orgId, project, env, body),
    {
      suppressToast: true,
      onSuccess: (meta) => {
        pushToast({ kind: "success", title: `Created ${meta.path}` });
        navigate(
          `/projects/${encodeURIComponent(project)}/envs/${encodeURIComponent(env)}/secrets/${encodeURIComponent(meta.path)}`,
        );
      },
      onError: (err) => {
        if (err instanceof WardkeyAPIError && err.status === 409) {
          setConflict({ path });
          return;
        }
        setServerError(extractError(err));
      },
    },
  );

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setTouched(true);
    setValueTouched(true);
    setServerError(null);
    setConflict(null);
    if (validatePath(path) !== null) return;
    if (validateValue(value) !== null) return;
    const valueString = value.kind === "text" ? value.text : value.base64;
    create.mutate({
      path,
      value: valueString,
      content_type: contentType,
      visibility,
    });
  }

  return (
    <div className="mx-auto max-w-[820px]">
      <PageHeader
        title="Create secret"
        subtitle={
          <span className="font-mono text-[12px] text-muted-foreground">
            {project}/{env}
          </span>
        }
      />

      <form className="fade-up space-y-6" onSubmit={handleSubmit}>
        <PathField
          path={path}
          envVarPreview={envVarPreview}
          error={pathError}
          onChange={(next) => {
            setPath(next);
            setServerError(null);
            setConflict(null);
          }}
          onBlur={() => setTouched(true)}
        />

        <ValueField
          valueKind={valueKind}
          value={value}
          contentType={contentType}
          onKindChange={setValueKind}
          onValueChange={(next) => {
            setValue(next);
            setValueTouched(true);
          }}
          onContentTypeChange={setContentType}
          error={valueError}
        />

        <ContentTypeField value={contentType} onChange={setContentType} />

        <VisibilityField value={visibility} onChange={setVisibility} />

        {conflict && (
          <div className="rounded-md border border-yellow-500/40 bg-yellow-500/5 p-4 text-[13px] text-muted-foreground">
            <div className="font-mono text-[10px] uppercase tracking-widest text-yellow-300">
              path already exists
            </div>
            <p className="mt-2">
              A secret already lives at{" "}
              <code className="font-mono text-foreground">{conflict.path}</code> in {project}/{env}.
            </p>
            <div className="mt-3 flex flex-wrap items-center gap-3">
              <Button asChild variant="outline" size="sm">
                <Link
                  to={`/projects/${encodeURIComponent(project)}/envs/${encodeURIComponent(env)}/secrets/${encodeURIComponent(conflict.path)}`}
                >
                  View existing secret
                </Link>
              </Button>
              <span className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground/70">
                add-as-new-version lands with WARD-80
              </span>
            </div>
          </div>
        )}

        {serverError && (
          <div className="rounded-md border border-destructive/40 bg-destructive/5 p-4 text-[13px] text-muted-foreground">
            <div className="font-mono text-[10px] uppercase tracking-widest text-destructive">
              create failed
            </div>
            <p className="mt-2">{serverError}</p>
          </div>
        )}

        <div className="flex items-center justify-end gap-3">
          <Button asChild variant="ghost" size="sm">
            <Link
              to={`/projects/${encodeURIComponent(project)}/envs/${encodeURIComponent(env)}/secrets`}
            >
              Cancel
            </Link>
          </Button>
          <Button
            type="submit"
            size="sm"
            disabled={
              create.isPending ||
              validatePath(path) !== null ||
              validateValue(value) !== null ||
              !contentType
            }
          >
            {create.isPending ? "Encrypting…" : "Create secret"}
            <span aria-hidden>→</span>
          </Button>
        </div>
      </form>
    </div>
  );
}

function PathField({
  path,
  envVarPreview,
  error,
  onChange,
  onBlur,
}: {
  path: string;
  envVarPreview: string | null;
  error: string | null;
  onChange: (next: string) => void;
  onBlur: () => void;
}) {
  return (
    <label className="block">
      <span className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
        path
      </span>
      <Input
        type="text"
        value={path}
        onChange={(e) => onChange(e.target.value)}
        onBlur={onBlur}
        placeholder="prod/db/password"
        spellCheck={false}
        autoCapitalize="off"
        autoCorrect="off"
        aria-label="Secret path"
        aria-invalid={error !== null}
        className="mt-2 font-mono"
      />
      <div className="mt-1.5 flex flex-wrap items-center justify-between gap-2 font-mono text-[10px] uppercase tracking-widest">
        <span className={error ? "text-destructive" : "text-muted-foreground/70"}>
          {error ?? "slash-separated, [A-Za-z0-9_-] per segment"}
        </span>
        {envVarPreview && (
          <span className="text-muted-foreground">
            env var: <span className="text-foreground">{envVarPreview}</span>
          </span>
        )}
      </div>
    </label>
  );
}

function ValueField({
  valueKind,
  value,
  contentType,
  onKindChange,
  onValueChange,
  onContentTypeChange,
  error,
}: {
  valueKind: ValueKind;
  value: ValueState;
  contentType: string;
  onKindChange: (kind: ValueKind) => void;
  onValueChange: (next: ValueState) => void;
  onContentTypeChange: (next: string) => void;
  error: string | null;
}) {
  const fileInputRef = useRef<HTMLInputElement>(null);

  async function handleFile(file: File) {
    if (file.size > MAX_VALUE_BYTES) {
      onValueChange({
        kind: "file",
        name: file.name,
        size: file.size,
        // Empty base64 so validation rejects without storing the giant blob.
        base64: "",
      });
      return;
    }
    const buf = new Uint8Array(await file.arrayBuffer());
    const base64 = uint8ToBase64(buf);
    onValueChange({ kind: "file", name: file.name, size: file.size, base64 });
    // Auto-detect content type from the extension on first drop. We never
    // overwrite a user's explicit pick — only set when it's still the default.
    const ext = file.name.toLowerCase().split(".").pop() ?? "";
    const detected = EXT_TO_CONTENT_TYPE[ext];
    if (detected && contentType === "text/plain") {
      onContentTypeChange(detected);
    } else if (!detected && contentType === "text/plain") {
      onContentTypeChange("application/octet-stream");
    }
  }

  return (
    <div>
      <div className="flex items-center justify-between">
        <span className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
          value
        </span>
        <div
          className="inline-flex rounded-md border border-border bg-card p-0.5 font-mono text-[10px] uppercase tracking-widest"
          role="tablist"
          aria-label="Value input mode"
        >
          {(["text", "file"] as ValueKind[]).map((k) => (
            <button
              key={k}
              type="button"
              role="tab"
              aria-selected={valueKind === k}
              onClick={() => onKindChange(k)}
              className={`rounded-sm px-3 py-1 transition-colors ${
                valueKind === k
                  ? "bg-muted text-foreground"
                  : "text-muted-foreground hover:text-foreground"
              }`}
            >
              {k}
            </button>
          ))}
        </div>
      </div>

      {valueKind === "text" ? (
        <Textarea
          aria-label="Secret value (text)"
          value={value.kind === "text" ? value.text : ""}
          onChange={(e) => onValueChange({ kind: "text", text: e.target.value })}
          placeholder="paste or type"
          rows={5}
          spellCheck={false}
          aria-invalid={error !== null}
          className="mt-2 font-mono resize-y"
        />
      ) : (
        <div className="mt-2">
          <div
            // biome-ignore lint/a11y/useSemanticElements: drag-drop target — semantic button doesn't accept dragover events the way we need
            role="button"
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                fileInputRef.current?.click();
              }
            }}
            onClick={() => fileInputRef.current?.click()}
            onDragOver={(e) => e.preventDefault()}
            onDrop={(e) => {
              e.preventDefault();
              const f = e.dataTransfer.files[0];
              if (f) void handleFile(f);
            }}
            className="flex cursor-pointer flex-col items-center justify-center gap-2 rounded-md border border-dashed border-border bg-card/40 px-4 py-8 transition-colors hover:bg-muted/50"
          >
            {value.kind === "file" && value.name ? (
              <>
                <div className="font-mono text-[13px] text-foreground">{value.name}</div>
                <div className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground/70">
                  {formatBytes(value.size)} · click to replace
                </div>
              </>
            ) : (
              <>
                <div className="font-mono text-[12px] text-muted-foreground">
                  drop a file or click
                </div>
                <div className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground/70">
                  max {Math.round(MAX_VALUE_BYTES / 1024)} kB
                </div>
              </>
            )}
            <input
              ref={fileInputRef}
              type="file"
              className="hidden"
              onChange={(e) => {
                const f = e.target.files?.[0];
                if (f) void handleFile(f);
              }}
              aria-label="Choose a file"
            />
          </div>
        </div>
      )}

      {error && (
        <p className="mt-2 font-mono text-[11px] text-destructive" role="alert">
          {error}
        </p>
      )}
    </div>
  );
}

function VisibilityField({
  value,
  onChange,
}: {
  value: Visibility;
  onChange: (next: Visibility) => void;
}) {
  const tiers: Visibility[] = ["open", "hidden", "sealed"];
  return (
    <div>
      <span className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
        visibility
      </span>
      <div
        className="mt-2 grid gap-2 sm:grid-cols-3"
        role="radiogroup"
        aria-label="Visibility tier"
      >
        {tiers.map((tier) => {
          const selected = value === tier;
          return (
            <button
              key={tier}
              type="button"
              // biome-ignore lint/a11y/useSemanticElements: custom-styled radio with description block — native <input type="radio"> can't host the layout
              role="radio"
              aria-checked={selected}
              onClick={() => onChange(tier)}
              className={`rounded-md border px-3 py-3 text-left transition-colors ${
                selected
                  ? "border-foreground/30 bg-muted"
                  : "border-border bg-card hover:bg-muted/50"
              }`}
            >
              <div
                className={`font-mono text-[11px] uppercase tracking-widest ${
                  selected ? "text-foreground" : "text-muted-foreground"
                }`}
              >
                {tier}
              </div>
              <div className="mt-1 text-[12px] leading-snug text-muted-foreground">
                {VISIBILITY_DESCRIPTIONS[tier]}
              </div>
            </button>
          );
        })}
      </div>
      <p className="mt-2 font-mono text-[10px] uppercase tracking-widest text-muted-foreground/70">
        sealed secrets are only readable by service tokens — humans can rotate or delete them
      </p>
    </div>
  );
}

function ContentTypeField({
  value,
  onChange,
}: {
  value: string;
  onChange: (next: string) => void;
}) {
  return (
    <div>
      <span className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
        content type
      </span>
      <div className="mt-2">
        <Select
          options={CONTENT_TYPES}
          value={value}
          onChange={onChange}
          ariaLabel="Content type"
        />
      </div>
    </div>
  );
}

// ─── helpers ────────────────────────────────────────────────────────────────

export function validatePath(path: string): string | null {
  if (path.length === 0) return "Path is required.";
  if (path.length > MAX_PATH_LEN) return `Max ${MAX_PATH_LEN} characters.`;
  if (path.startsWith("/") || path.endsWith("/")) {
    return "No leading or trailing slash.";
  }
  if (path.includes("//")) return "No empty segments.";
  const segments = path.split("/");
  for (const seg of segments) {
    if (!SEGMENT_RE.test(seg)) {
      return `Segment "${seg}" — letters, digits, hyphen, underscore only.`;
    }
  }
  return null;
}

export function pathToEnvVar(path: string): string | null {
  if (path.length === 0) return null;
  // Replace [^a-z0-9] with _ and uppercase — what most "secrets-to-env-vars"
  // helpers do (Doppler, dotenv-friendly).
  const cleaned = path.replace(/[^a-z0-9]/gi, "_").toUpperCase();
  return cleaned || null;
}

function validateValue(value: ValueState): string | null {
  if (value.kind === "text") {
    if (value.text.length === 0) return "Value is required.";
    // Worst-case UTF-8 byte length ≈ 4 × char count; cheap upper bound.
    const bytes = new TextEncoder().encode(value.text).byteLength;
    if (bytes > MAX_VALUE_BYTES) {
      return `Value is ${formatBytes(bytes)} — max ${formatBytes(MAX_VALUE_BYTES)}.`;
    }
    return null;
  }
  if (value.size === 0) return "Empty file.";
  if (value.size > MAX_VALUE_BYTES) {
    return `File is ${formatBytes(value.size)} — max ${formatBytes(MAX_VALUE_BYTES)}.`;
  }
  return null;
}

function extractError(err: WardkeyAPIError | Error): string {
  if (err instanceof WardkeyAPIError) {
    if (err.code === "forbidden") return "You don't have write access to this path.";
    return err.message || err.code || "Request failed";
  }
  return err.message || "Request failed";
}

function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} kB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

function uint8ToBase64(bytes: Uint8Array): string {
  // btoa expects a binary string; we chunk to avoid blowing the call stack on
  // larger inputs. 64 KB max means ~8000 chars per chunk is safe.
  const CHUNK = 0x8000;
  let binary = "";
  for (let i = 0; i < bytes.length; i += CHUNK) {
    binary += String.fromCharCode(...bytes.subarray(i, i + CHUNK));
  }
  return btoa(binary);
}
